import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useReveal } from '../components/marketing/motion'
import rawIndex from '../data/vessels-index.json'
import { usePageMeta } from '../lib/usePageMeta'

interface VesselIndexRow {
  slug: string
  name: string
  imo: string
  ship_type: string | null
  teu: number | null
  operator: string | null
}
const INDEX = rawIndex as VesselIndexRow[]

export function VesselsHubPage() {
  usePageMeta(
    `Container ship database: ${INDEX.length} vessels, specs & tracking · SchedulesMCP`,
    `Particulars and last-known positions for ${INDEX.length} container ships: capacity, dimensions, flag, build year and live tracking.`
  )
  useReveal('main')
  const [q, setQ] = useState('')
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const base = needle
      ? INDEX.filter((v) => v.name.toLowerCase().includes(needle) || String(v.imo).includes(needle))
      : INDEX
    return base.slice(0, 300)
  }, [q])

  return (
    <main className="max-w-5xl mx-auto px-6 pt-12 pb-16">
      {/* Hero (book §07 anatomy): teal eyebrow → ultralight title → one-line hook → live lead */}
      <header className="text-center mb-14">
        <p
          className="m-0 font-mono text-xs uppercase text-accent"
          style={{ letterSpacing: '0.14em' }}
        >
          Vessel database
        </p>
        <h1
          className="mx-auto mt-10 mb-0 font-display text-[clamp(28px,4.6vw,48px)] leading-[1.08] text-[var(--text-primary)]"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Container ship particulars and positions
        </h1>
        <p className="mx-auto mt-10 mb-0 text-base leading-[1.5] text-[var(--text-secondary)]">
          Every tracked container ship, normalised into one shape with capacity, dimensions, flag
          and last-known position.
        </p>
        <p className="mx-auto mt-6 mb-0 max-w-[62ch] text-sm leading-relaxed text-[var(--text-muted)]">
          Particulars and last-known positions for {INDEX.length.toLocaleString('en-US')} container
          ships and cargo vessels: capacity, dimensions, flag, build year and live tracking. Ranked
          by TEU capacity; the largest container ships lead.
        </p>
        <form
          onSubmit={(e) => e.preventDefault()}
          className="mx-auto mt-10 flex w-full max-w-md items-stretch justify-center gap-2"
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by vessel name or IMO…"
            className="w-full flex-1 rounded-lg border border-border bg-surface-1 px-4 py-2.5 font-mono text-sm"
          />
          <button
            type="submit"
            className="rounded-lg bg-signal text-on-accent font-semibold px-5 py-2.5 no-underline hover:opacity-90"
          >
            Search
          </button>
        </form>
      </header>
      <div className="overflow-x-auto border border-border rounded-xl">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                Vessel
              </th>
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                IMO
              </th>
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                TEU
              </th>
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                Type
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((v) => (
              <tr key={v.slug} className="border-b border-border">
                <td className="p-3">
                  <Link
                    to={`/vessels/${v.slug}`}
                    className="text-signal font-semibold no-underline hover:underline"
                  >
                    {v.name}
                  </Link>
                </td>
                <td className="p-3 text-[var(--text-muted)] font-mono">{v.imo}</td>
                <td className="p-3 text-[var(--text-muted)] font-mono">
                  {v.teu != null ? v.teu.toLocaleString('en-US') : '—'}
                </td>
                <td className="p-3 text-[var(--text-muted)]">{v.ship_type || 'Container ship'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[var(--text-muted)] text-xs mt-4">
        Showing {shown.length} of {INDEX.length.toLocaleString('en-US')} vessels
        {q ? ' matching your search' : ' (largest by capacity)'}.
      </p>
    </main>
  )
}
